import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MapPin, Clock, Bookmark, BookmarkCheck } from 'lucide-react';
import { useMapStore } from '../store/mapStore';
import { STATUS } from '../types';

const SearchResults = () => {
  // --- stacked marker UI helpers ---
  const getStackSummary = (loc) =>
    loc?._stackSummary || { ACTIVE: 0, PROSPECT: 0, TARGET: 0, SPECIALTY: 0, ACTIVEOLDER: 0 };
  const getStackCount = (loc) => Number(loc?._stackCount || 0);

  const StackBlips = ({ summary }) => {
  const s = summary || {};
  const dots = [];
 
  const pushDots = (label, count, cls) => {
    const n = Math.max(0, Number(count || 0));
    for (let i = 0; i < n && dots.length < 3; i++) {
      dots.push({ label, cls });
    }
  };
 
  // Order matters (most important first)
  pushDots('TARGET', s.TARGET, 'bg-purple-500');
  pushDots('SPECIALTY', s.SPECIALTY, 'bg-yellow-500');
  pushDots('PROSPECT', s.PROSPECT, 'bg-blue-500');
  pushDots('ACTIVEOLDER', s.ACTIVEOLDER, 'bg-red-500');
  pushDots('ACTIVE', s.ACTIVE, 'bg-green-500');
 
  return (
    <div className="mt-1 flex items-center gap-1">
      {dots.map((d, idx) => (
        <span
          key={`${d.label}-${idx}`}
          className={`inline-block h-2 w-2 rounded-full ${d.cls}`}
          title={d.label}
        />
      ))}
    </div>
  );
};


  const { locations, filteredLocations, setSelectedLocation, globalSearchMode, GlobalDeviceLocation, GlobalMapCenter, currentSearchTerm, showLeads, isFavorite, toggleFavorite, showBookmarksOnly } = useMapStore();
  const isMobile = window.innerWidth <= 800;
  const [resultsFilter, setResultsFilter] = useState("");
  const resultsFilterLower = resultsFilter.trim().toLowerCase();
  const favCount = useMapStore(s =>
    Array.isArray(s.favoritesIds) ? s.favoritesIds.length : 0
  );


  const rows = React.useMemo(() => {
    let base = Array.isArray(filteredLocations) ? filteredLocations : [];

    if (!showLeads) {
      const isSFLead = (r) => {
        const status = ((r.shipStatus ?? r.status) ?? '').toString().toLowerCase();
        const scope = (r.scope ?? '').toString().trim().toUpperCase();
        return status === 'target' && scope === 'SALESFORCE';
      };
      base = base.filter(r => !isSFLead(r));
    }
    if (resultsFilterLower) {
      base = base.filter((r) => {
        const name =
          (r?.name || r?.companyName || "").toString().toLowerCase();
        return name.includes(resultsFilterLower);
      });
    }
    const gated = showBookmarksOnly ? base.filter(isFavorite) : base;
    console.log('[SearchResults] base=', base.length, 'gated=', gated.length, 'bookmarksOnly=', showBookmarksOnly);
    return gated;
  }, [filteredLocations, showLeads, showBookmarksOnly, isFavorite]);



  const getMarkerIcon = (location) => {
    const status = location.shipStatus

    let business_unit = '';

    if (location?.businessUnit) {
      business_unit = location.businessUnit;
    }
    else if (location?.accountDetails?.businessUnit) {
      business_unit = location.accountDetails.businessUnit;
    }

    if (Number(location?._stackCount || 0) > 1) {
      return '/markers/stacked-marker.svg';
    }

    // If it's not US Broadline, it's considered Specialty — return early
    if (business_unit !== 'US Broadline' && status !== 'target') {
      return '/markers/specialty-marker.svg';
    }
    //console.log("Search Results", status, business_unit)
    // For US Broadline, use switch on status
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '/markers/active-marker.svg';
      case 'ACTIVEOLDER':
        return '/markers/active-older.svg';
      case 'PROSPECT':
        return '/markers/prospect-marker.svg';
      case 'TARGET':
        return '/markers/target-marker.svg';
      default:
        return '/markers/default.svg';
    }
  };

  const handleLocationClick = (location) => {
    const c = Number(location?._stackCount || 0);
    if (c > 1) {
      console.log('[SearchResults] stacked click:', {
        name: location?.name ?? location?.companyName,
        stackCount: c,
        members: Array.isArray(location?._stackMembers) ? location._stackMembers.length : 0,
      });
    }
    setSelectedLocation(location);
  };



  // if (filteredLocations.length === 0) {
  //   return (
  //     <div className="p-6 text-center">
  //       <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
  //       <h3 className="text-fluid-lg font-medium mb-2">No matching locations</h3>
  //       <p className="text-gray-500 dark:text-gray-400">
  //         Try adjusting your filters to see more results.
  //       </p>
  //     </div>
  //   );
  // }
  const center = GlobalDeviceLocation || GlobalMapCenter;
  const searchTerm = currentSearchTerm?.toLowerCase() || '';

  // console.log('Search mode:', globalSearchMode);
  // console.log('Search center:', center);
  // console.log('Search term:', searchTerm);

  const getDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const filteredRows = React.useMemo(() => {
    if (!resultsFilterLower) return rows;

    return rows.filter((r) => {
      const name =
        (r?.name || r?.companyName || "").toLowerCase();


      return (
        name.includes(resultsFilterLower));
    });
  }, [rows, resultsFilterLower]);
  const sortedResults = [...filteredRows].sort((a, b) => {
    const distA = center ? getDistance(center.lat, center.lng, a.lat, a.lng) : Infinity;
    const distB = center ? getDistance(center.lat, center.lng, b.lat, b.lng) : Infinity;

    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    const matchA = nameA.includes(searchTerm) ? 0 : 1;
    const matchB = nameB.includes(searchTerm) ? 0 : 1;

    // console.log(`[${a.name}] distA: ${distA.toFixed(2)}km match: ${matchA}`);
    // console.log(`[${b.name}] distB: ${distB.toFixed(2)}km match: ${matchB}`);

    if (globalSearchMode === 'account') {
      return distA - distB;
    } else if (globalSearchMode === 'location') {
      if (matchA !== matchB) return matchA - matchB;
      return distA - distB;
    }

    return 0;
  });


  if (locations.length === 0) {
    return (
      <div className="p-6 text-center">
        <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-fluid-lg font-medium mb-2">No locations found</h3>
        <p className="text-gray-500 dark:text-gray-400">
          Try searching for a ZIP code or use the advanced search options.
        </p>
      </div>
    );
  }
  // If rows is empty now, show a toggle-aware message
  if (rows.length === 0) {
    return (
      <div className="p-6 text-center">
        <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-fluid-lg font-medium mb-2">
          {showBookmarksOnly ? 'No bookmarked locations' : 'No matching locations'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {showBookmarksOnly
            ? 'Save some favorites, or turn off Bookmarks-only.'
            : 'Try adjusting your filters to see more results.'}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      <div className="p-3 border-b border-gray-200 sticky top-0 bg-white z-10">
        <input
          value={resultsFilter}
          onChange={(e) => {
            setResultsFilter(e.target.value);
            console.log("[ResultsFilter] term:", e.target.value);
          }}
          placeholder="Filter results by name…"
          className="w-full rounded-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {sortedResults.map((location) => {

        const fav = isFavorite(location);
        return (
          <div
            key={location.id}
            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            onClick={() => handleLocationClick(location)}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3 mt-1">
                <img
                  src={getMarkerIcon(location)}
                  alt="Location marker"
                  className="h-6 w-6"
                />
              </div>
              <div className="flex-1 min-w-0">
                {(() => {
                  const stackCount = getStackCount(location);
                  const isStacked = stackCount > 1;
                  const stackSummary = isStacked ? getStackSummary(location) : null;

                  return (
                    <>
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-medium truncate">
                          {location?.name ?? location?.companyName ?? 'Unnamed'}
                        </h3>

                        {isStacked && (
                          <span
                            className="flex-shrink-0 text-fluid-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
                            title={`${stackCount} accounts at this location`}
                          >
                            {stackCount}
                          </span>
                        )}
                      </div>

                      <p className="text-fluid-sm text-gray-600 dark:text-gray-400">
                        {location.address}
                      </p>

                      {isStacked && <StackBlips summary={stackSummary} />}
                    </>
                  );
                })()}
                {location.accountDetails && (
                  <div className="mt-1 flex items-center text-fluid-xs text-gray-500 dark:text-gray-400">
                    <span className="mr-2">{location.accountDetails.accountNumber}</span>
                    <span className="mr-2">•</span>
                    <span>{location.accountDetails.type}</span>
                  </div>
                )}
              </div>

              {/* Favorite button */}
              <div className="ml-3">
                <button
                  type="button"
                  className="p-2 -m-2 rounded-full hover:bg-gray-100 focus:outline-none"
                  aria-pressed={fav}
                  aria-label={fav ? 'Remove from favorites' : 'Save to favorites'}
                  onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onClick={(e) => {
                    console.log('[fav-btn] click on', location?.name, fav);
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('[fav-btn] propagation stopped, calling toggleFavorite');
                    toggleFavorite(location);
                    console.log('[fav-btn] toggleFavorite dispatched');
                  }}
                >
                  {fav ? (
                    <BookmarkCheck className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Bookmark className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SearchResults;
